import { useState, useEffect } from "react";
import {
  Calendar,
  AlertCircle,
  Send,
  FileText,
  CheckCircle,
  GraduationCap,
  Target,
  Lock,
} from "lucide-react";
import axios from "axios";
import selectHomework from "../constants/content_chapter.json";
import { useSelector } from "react-redux";
import api from "../utils/api";

function HomeworkModal({ classData, teacherData }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [exerciseTemplate, setExerciseTemplate] = useState([]);
  const [error, setError] = useState("");

  const classInfo = useSelector((state) => state.selectClass.value);
  const classId = classInfo._id;

  // 1. OTOMATİK SINIF EŞLEŞTİRME (className: "2/C" -> "class 2")
  useEffect(() => {
    if (classData?.className) {
      // className içindeki ilk rakamı bulur (2, 3 veya 4)
      const match = classData.className.match(/\d+/);
      if (match) {
        const classLevel = match[0];
        const targetClassId = `class ${classLevel}`;

        // JSON içinde bu ID var mı kontrol et ve set et
        const classExists = selectHomework.classes.find(
          (cls) => cls.id === targetClassId,
        );
        if (classExists) {
          setSelectedClass(targetClassId);
        }
      }
    }
  }, [classData]);

  const getContentChapter = async (className, unites, classId) => {
    try {
      //bir classın içerikleri getirir bunun içide chapter ve task mevcut
      const res = await api.get("getClassContent", {
        params: { className, unites, classId },
      });
      const chapterNames = res.data.data.map((item) => item.chapter);
      setExerciseTemplate(chapterNames);
    } catch (error) {
      console.error("Veri çekerken hata:", error);
      setExerciseTemplate([]);
    }
  };

  useEffect(() => {
    const handleChapter = async () => {
      if (selectedClass && selectedChapter) {
        await getContentChapter(selectedClass, selectedChapter, classId);
      }
    };
    handleChapter();
  }, [selectedClass, selectedChapter, classId]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    dueDate: "",
    priority: "medium",
  });

  const chapters = selectedClass
    ? selectHomework.classes.find((cls) => cls.id === selectedClass)?.chapters
    : [];

  const exercises = selectedChapter
    ? chapters?.find((ch) => ch.id === selectedChapter)?.exercises
    : [];

  const resolvedExercises =
    exercises === "@exerciseTemplate" ? exerciseTemplate : exercises || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Başlık ve Konu Otomatik Oluşturma
  useEffect(() => {
    if (selectedClass && selectedChapter && selectedExercises.length > 0) {
      const className = selectHomework.classes.find(
        (cls) => cls.id === selectedClass,
      )?.name;
      const chapterName = chapters?.find(
        (ch) => ch.id === selectedChapter,
      )?.name;
      const exerciseText =
        selectedExercises.length === 1
          ? selectedExercises[0]
          : `${selectedExercises.length} Egzersiz`;

      setFormData((prev) => ({
        ...prev,
        title: `${className} - ${chapterName} - ${exerciseText}`,
        subject: `${className} - ${chapterName}`,
      }));
    }
  }, [selectedClass, selectedChapter, selectedExercises, chapters]);

  const isFormValid =
    formData.title.trim() &&
    formData.description.trim() &&
    formData.dueDate &&
    selectedClass &&
    selectedChapter &&
    selectedExercises.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      setError("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const className = selectHomework.classes.find(
        (cls) => cls.id === selectedClass,
      )?.name;
      const chapterName = chapters?.find(
        (ch) => ch.id === selectedChapter,
      )?.name;

      const homeworkData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        classId: classData._id,
        teacherId: teacherData._id || teacherData.id,
        teacherName: teacherData.name || teacherData.username,
        className: classData.className,
        subject: formData.subject.trim(),
        dueDate: formData.dueDate,
        priority: formData.priority,
        selectedClass: { id: selectedClass, name: className },
        selectedChapter: { id: selectedChapter, name: chapterName },
        selectedExercises: selectedExercises,
      };
      console.log(homeworkData, "datahomework");
      const response = await api.post(
        "notification/homework/create",
        homeworkData,
      );

      if (response.data.success) setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.message || "Ödev oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  return (
    <div className="bg-white rounded-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
      {/* Status Messages */}
      {success && (
        <div className="m-6 p-5 bg-emerald-50 border border-green-200 rounded-xl flex items-center space-x-3">
          <div className="bg-green-500 p-2 rounded-full">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-green-800 font-bold">
              Ödev Başarıyla Gönderildi!
            </p>
            <p className="text-green-700 text-sm">
              {classData?.className} sınıfındaki öğrencilere iletildi.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="m-6 p-5 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-6">
        {/* Selection Section */}
        <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
          <div className="flex items-center space-x-2 border-b border-gray-200 pb-3">
            <GraduationCap className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-800">
              İçerik ve Müfredat Seçimi
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sınıf Seçimi - Kilitli ve Otomatik */}
            <div>
              <label className="flex items-center text-sm font-semibold mb-2 text-gray-700">
                Sınıf Seviyesi <Lock className="w-3 h-3 ml-2 text-gray-400" />
              </label>
              <div className="relative">
                <select
                  value={selectedClass || ""}
                  disabled={true}
                  className="w-full bg-gray-100 border border-gray-300 rounded-xl px-4 py-3 text-gray-500 cursor-not-allowed appearance-none"
                >
                  <option value="">Sınıf Belirleniyor...</option>
                  {selectHomework.classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-[10px] text-indigo-500 mt-1 font-medium">
                * {classData?.className} sınıfına göre otomatik seçildi.
              </p>
            </div>

            {/* Bölüm Seçimi */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Bölüm / Konu *
              </label>
              <select
                value={selectedChapter}
                onChange={(e) => {
                  setSelectedChapter(e.target.value);
                  setSelectedExercises([]);
                }}
                disabled={!selectedClass || loading || success}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              >
                <option value="">Bölüm seçin</option>
                {chapters?.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Egzersiz Listesi */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Egzersizler (Çoklu Seçim)
              </label>
              <div className="border border-gray-300 rounded-xl p-3 max-h-48 overflow-y-auto bg-white shadow-sm">
                {!selectedChapter ? (
                  <p className="text-gray-400 text-sm italic">
                    Lütfen önce bir konu seçin...
                  </p>
                ) : resolvedExercises.length > 0 ? (
                  <div className="space-y-1">
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
                      <span className="text-xs font-bold text-indigo-600">
                        {selectedExercises.length} Seçildi
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedExercises([...resolvedExercises])
                        }
                        className="text-[10px] uppercase font-bold text-gray-500 hover:text-indigo-600"
                      >
                        Tümünü Seç
                      </button>
                    </div>
                    {resolvedExercises.map((ex, idx) => (
                      <label
                        key={idx}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-indigo-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedExercises.includes(ex)}
                          onChange={(e) => {
                            if (e.target.checked)
                              setSelectedExercises((prev) => [...prev, ex]);
                            else
                              setSelectedExercises((prev) =>
                                prev.filter((item) => item !== ex),
                              );
                          }}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                        <span className="text-sm text-gray-700">{ex}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-red-400 text-sm">İçerik bulunamadı.</p>
                )}
              </div>
            </div>

            {/* Önizleme Paneli */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-bold text-indigo-800 uppercase">
                  Ödev Özeti
                </span>
              </div>
              {selectedExercises.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                    {formData.title}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedExercises.map((ex, i) => (
                      <span
                        key={i}
                        className="bg-white text-[10px] px-2 py-1 rounded border border-indigo-200 text-indigo-700"
                      >
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400">Henüz içerik seçilmedi.</p>
              )}
            </div>
          </div>
        </div>

        {/* Detaylar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              <Calendar className="w-4 h-4 inline mr-2" /> Teslim Tarihi
            </label>
            <input
              type="date"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleInputChange}
              min={getMinDate()}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              <AlertCircle className="w-4 h-4 inline mr-2" /> Öncelik
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="low">Düşük</option>
              <option value="medium">Orta</option>
              <option value="high">Yüksek</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2 text-gray-700">
            <FileText className="w-4 h-4 inline mr-2" /> Talimatlar
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            placeholder="Öğrenciler için not ekleyin..."
            className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            className="flex-1 py-3 px-4 border border-gray-300 rounded-xl font-bold text-gray-600 hover:bg-gray-50 transition-all"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={loading || success || !isFormValid}
            className="flex-[2] bg-indigo-600 text-white py-3 px-4 rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center space-x-2 shadow-lg shadow-indigo-100"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Send className="w-4 h-4" /> <span>Ödevi Gönder</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default HomeworkModal;
